import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ivLogo from '@/assets/iv-talent-logomark.png';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { ArrowRight, Loader2, ShieldOff, Info } from 'lucide-react';

type Step = 'email' | 'code' | 'denied';

async function parseFunctionError(error: any): Promise<{ status?: number; message?: string }> {
  const ctx = error?.context;
  if (!ctx) return { message: error?.message };
  try {
    const body = await ctx.clone().json();
    return { status: ctx.status, message: body?.error ?? error?.message };
  } catch {
    return { status: ctx.status, message: error?.message };
  }
}

export default function SignIn() {
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(() => (params.get('email') ?? '').trim().toLowerCase());
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [codeNotice, setCodeNotice] = useState<string | null>(null);
  const navigate = useNavigate();

  const rateNoticeRef = useRef<string | null>(null);

  const requestCode = async (): Promise<'ok' | 'denied' | 'invalid' | 'rate' | 'error'> => {
    setEmailNotice(null);
    const { data, error } = await supabase.functions.invoke('request-signin-code', {
      body: { email: email.trim().toLowerCase() },
    });
    if (!error && !(data && (data as any).error)) return 'ok';

    // Recover the friendly body from the non-2xx response.
    const parsed = error ? await parseFunctionError(error) : { status: 200, message: (data as any)?.error };
    const status = parsed.status;
    const msg = parsed.message ?? '';

    if (status === 403 || /access list/i.test(msg)) return 'denied';
    if (status === 400) return 'invalid';
    if (status === 429 || /rate limit|only request this after|please wait/i.test(msg)) {
      rateNoticeRef.current = msg || 'Please wait a moment before requesting another code.';
      return 'rate';
    }
    console.error('[auth/signin] request-signin-code failed', { status, msg });
    return 'error';
  };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const result = await requestCode();
    setSending(false);
    if (result === 'ok') {
      setStep('code');
    } else if (result === 'denied') {
      setStep('denied');
    } else if (result === 'invalid') {
      setEmailNotice('Please enter a valid email address.');
    } else if (result === 'rate') {
      setEmailNotice(rateNoticeRef.current ?? 'Please wait a moment before requesting another code.');
    } else {
      setEmailNotice('We could not send your code. Try again in a moment.');
    }
  };

  const verifyCode = async (token: string) => {
    setVerifying(true);
    setCodeNotice(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: 'email',
    });
    setVerifying(false);
    if (error) {
      const m = (error.message || '').toLowerCase();
      if (m.includes('expired')) {
        setCodeNotice('That code has expired. Send a new one.');
      } else {
        setCodeNotice("That code didn't match. Check it and try again, or send a new one.");
      }
      setCode('');
      return;
    }
    const next = params.get('next');
    const cbHref = next ? `/auth/callback?next=${encodeURIComponent(next)}` : '/auth/callback';
    navigate(cbHref, { replace: true });
  };

  const onCodeChange = (v: string) => {
    setCode(v);
    if (v.length === 8 && !verifying) {
      void verifyCode(v);
    }
  };

  const resend = async () => {
    setSending(true);
    setCodeNotice(null);
    const result = await requestCode();
    setSending(false);
    if (result === 'ok') {
      setCodeNotice(`A new code was sent to ${email}.`);
    } else if (result === 'denied') {
      setStep('denied');
    } else if (result === 'rate') {
      setCodeNotice(rateNoticeRef.current ?? 'Please wait a moment before requesting another code.');
    } else {
      setCodeNotice('Could not resend the code. Please try again in a moment.');
    }
  };

  const resetToEmail = () => {
    setStep('email');
    setCode('');
    setEmail('');
    setEmailNotice(null);
    setCodeNotice(null);
  };

  return (
    <main className="min-h-screen bg-cream text-ink font-grotesk antialiased grid md:grid-cols-12">
      {/* Editorial panel */}
      <aside className="hidden md:flex md:col-span-7 relative flex-col justify-between p-10 lg:p-14 bg-cream-warm border-r border-hairline overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full opacity-[0.08] blur-3xl"
          style={{ background: 'radial-gradient(circle, #E7831D 0%, transparent 70%)' }}
        />
        <Link to="/" aria-label="Home" className="relative inline-flex items-center gap-2.5 group w-fit">
          <img src={ivLogo} alt="IV Talent" className="h-9 w-9 rounded-md" />
          <span className="font-display text-[20px] tracking-tight text-ink">CultureLens</span>
        </Link>

        <div className="relative max-w-[36ch]">
          <h1 className="font-display text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.025em] text-ink">
            Welcome back.
          </h1>
          <p className="mt-5 text-[16px] lg:text-[17px] text-charcoal/75 leading-[1.6]">
            Your daily read is waiting.
          </p>
        </div>

        <div className="relative flex items-center justify-between text-[13px] text-charcoal/60">
          <span>New to CultureLens?</span>
          <GetStartedDialog>
            <button className="inline-flex items-center gap-1.5 text-ink hover:text-ink transition-colors font-medium">
              Request access <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </GetStartedDialog>
        </div>
      </aside>

      {/* Form panel */}
      <section className="md:col-span-5 flex flex-col items-center justify-center px-6 py-14 md:py-10">
        <div className="w-full max-w-sm">
          <Link to="/" aria-label="Home" className="md:hidden mb-10 inline-flex items-center gap-2.5">
            <img src={ivLogo} alt="IV Talent" className="h-9 w-9 rounded-md" />
            <span className="font-display text-[18px] tracking-tight text-ink">CultureLens</span>
          </Link>

          {step === 'denied' ? (
            <div>
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-cream-warm border border-hairline text-ink/70">
                <ShieldOff className="h-5 w-5" aria-hidden />
              </div>
              <h2 className="mt-5 font-display text-[28px] md:text-[32px] leading-[1.1] tracking-[-0.02em] text-ink">
                We couldn’t sign you in
              </h2>
              <p className="mt-3 text-[14px] text-charcoal/75 leading-relaxed">
                <span className="text-ink">{email}</span> is not on the access list. Ask your admin to invite you, then try again.
              </p>
              <div className="mt-8 space-y-3">
                <GetStartedDialog>
                  <Button
                    type="button"
                    className="group w-full h-12 rounded-full bg-ink hover:bg-ink-soft text-cream text-[15px] font-medium hover:-translate-y-px transition-transform duration-300"
                  >
                    Request access <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </GetStartedDialog>
                <button
                  type="button"
                  onClick={resetToEmail}
                  className="w-full py-3 text-[14px] text-charcoal hover:text-ink transition-colors"
                >
                  Try a different email
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <h2 className="font-display text-[28px] md:text-[32px] leading-[1.1] tracking-[-0.02em] text-ink">
                  Sign in
                </h2>
                <p className="mt-2 text-[14px] text-charcoal/70 leading-relaxed">
                  {step === 'email'
                    ? 'We will email you an 8-digit code. No password.'
                    : (
                        <>
                          Enter the 8-digit code sent to{' '}
                          <span className="text-ink">{email}</span>.
                        </>
                      )}
                </p>
              </div>

              <div className="mt-8">
                {step === 'email' ? (
                  <form onSubmit={sendCode} className="space-y-4">
                    <Input
                      type="email"
                      required
                      autoFocus
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailNotice) setEmailNotice(null); }}
                      disabled={sending}
                      className="h-12 rounded-full border-hairline bg-cream/60 px-5 text-[15px] focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-0"
                    />
                    {emailNotice && (
                      <p className="flex items-start gap-2 text-[13px] text-charcoal/75 leading-relaxed px-1">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-charcoal/60" aria-hidden />
                        <span>{emailNotice}</span>
                      </p>
                    )}
                    <Button
                      type="submit"
                      disabled={sending || !email}
                      className="group w-full h-12 rounded-full bg-ink hover:bg-ink-soft text-cream text-[15px] font-medium hover:-translate-y-px transition-transform duration-300"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Send code <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={8}
                        value={code}
                        onChange={onCodeChange}
                        disabled={verifying}
                        autoFocus
                      >
                        <InputOTPGroup>
                          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <InputOTPSlot key={i} index={i} />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {verifying && (
                      <div className="flex items-center justify-center gap-2 text-[13px] text-charcoal/70">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying…
                      </div>
                    )}
                    {codeNotice && !verifying && (
                      <p className="flex items-start gap-2 text-[13px] text-charcoal/75 leading-relaxed px-1 justify-center text-center">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-charcoal/60" aria-hidden />
                        <span>{codeNotice}</span>
                      </p>
                    )}
                    <div className="flex flex-col gap-1 pt-2 border-t border-hairline">
                      <button
                        type="button"
                        onClick={resend}
                        disabled={sending || verifying}
                        className="w-full py-3 text-[14px] text-charcoal hover:text-ink transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resend code'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setStep('email');
                          setCode('');
                          setCodeNotice(null);
                        }}
                        disabled={verifying}
                        className="w-full py-3 text-[14px] text-charcoal hover:text-ink transition-colors disabled:opacity-50"
                      >
                        Use a different email
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="mt-10 md:hidden text-center text-[13px] text-charcoal/65">
            New to CultureLens?{' '}
            <GetStartedDialog>
              <button className="text-ink font-medium hover:text-ink transition-colors">
                Request access
              </button>
            </GetStartedDialog>
          </div>
        </div>
      </section>
    </main>
  );
}
